/*
 * Red Jar skincare related-product selector.
 *
 * Shopify stores Pack of 2 / Jar + Refill as separate products, rather than
 * variants of one product. Keep those relationships in this one small map so
 * they can be adjusted without changing Shopify product or variant data.
 */
(() => {
  // The ATC drawer represents purchase choices (single / pack / refill).
  // Keep it separate from the PDP Size / Type selector below.
  const ATC_GROUPS = {
    'numour-collagen-bombshell-jelly-smart-jar': {
      heading: 'Select variant',
      options: [
        { handle: 'numour-collagen-bombshell-jelly-smart-jar', label: '1 Jar', detail: '30g smart jar' },
        { handle: 'pack-of-2-numour-collagen-bombshell-red-led-smart-jar-duo', label: '2 Jars', detail: '2 × 30g jars · 60g', default: true },
        { handle: 'pack-of-2-collagen-bombshell-smart-duo-red-led-smart-jar-refill', label: 'Jar + Refill', detail: 'Smart jar + refill pod · 60g' },
      ],
    },
    'numour-damn-dewy': {
      heading: 'Select variant',
      options: [
        { handle: 'numour-damn-dewy', label: '1 Jar', detail: '30g smart jar' },
        { handle: 'pack-of-2-damn-dewy-smart-jar-duo', label: '2 Jars', detail: '2 × 30g jars · 60g', default: true },
        { handle: 'damn-dewy-smart-duo-led-jar-refill', label: 'Jar + Refill', detail: 'Smart jar + refill pod · 60g' },
      ],
    },
    'numour-dewy-mist-go': {
      heading: 'Select variant',
      options: [
        { handle: 'numour-dewy-mist-go', label: '1 Bottle', detail: 'Dewy Mist & Go' },
        { handle: 'numour-dewy-mist-go-pack-of-2', label: '2 Bottles', detail: 'Dewy Mist & Go Pack of 2', default: true },
      ],
    },
    'numour-collagen-barrier-defense-sunscreen': {
      heading: 'Select variant',
      options: [
        { handle: 'numour-collagen-barrier-defense-sunscreen', label: '1 Sunscreen', detail: 'Collagen Barrier Defense' },
        { handle: 'numour-collagen-barrier-defense-sunscreen-pack-of-2', label: '2 Sunscreens', detail: 'Collagen Barrier Defense Pack of 2', default: true },
      ],
    },
  };

  // The PDP selector represents form-factor choices. More product families can
  // be added once their exact Shopify product mappings are confirmed.
  const PDP_GROUPS = {
    'numour-collagen-bombshell-jelly-smart-jar': {
      heading: 'Size / type',
      options: [
        { handle: 'numour-collagen-bombshell-jelly-smart-jar', label: 'Smart Jar', detail: '30g Smart Jar · Red LED + sonic', default: true, displayName: 'Collagen Bombshell', displaySize: '30g' },
        { handle: 'numour-collagen-bombshell-refill-30g', label: 'Refill', detail: '30g refill · use with Smart Jar', displayName: 'Collagen Bombshell Refill', displaySize: '30g' },
        { handle: 'numour-collagen-bombshell-travel-pack', label: 'Travel', detail: '10g trial and travel pack', displayName: 'Collagen Bombshell Travel', displaySize: '10g' },
      ],
    },
    'numour-damn-dewy': {
      heading: 'Size / type',
      options: [
        { handle: 'numour-damn-dewy', label: 'Smart Jar', detail: '30g Smart Jar · Blue LED', default: true, displayName: 'Damn Dewy', displaySize: '30g' },
        { handle: 'numour-damn-dewy-small', label: 'Refill', detail: '30g refill · use with Smart Jar', displayName: 'Damn Dewy Refill', displaySize: '30g' },
      ],
    },
  };

  const productCache = new Map();
  let activeSheet = null;
  let sheetTrigger = null;
  let lockedScrollY = 0;
  let cartSyncInFlight = false;

  const rootUrl = () => (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
  const productUrl = (handle) => `${rootUrl()}/products/${encodeURIComponent(handle)}.js`;
  const cartUrl = (path) => `${rootUrl()}${path}`;
  const money = (paise) => new Intl.NumberFormat('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  }).format((Number(paise) || 0) / 100);

  async function getProduct(handle) {
    if (!productCache.has(handle)) {
      productCache.set(handle, fetch(productUrl(handle), { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Unable to load ${handle}`);
          return response.json();
        }));
    }
    return productCache.get(handle);
  }

  function selectedVariant(product) {
    return product.variants.find((variant) => variant.available) || product.variants[0];
  }

  async function hydrateOptions(group) {
    return Promise.all(group.options.map(async (option) => {
      const product = await getProduct(option.handle);
      const variant = selectedVariant(product);
      return {
        ...option,
        product,
        variant,
        available: Boolean(variant?.available),
        image: option.image || variant?.featured_image?.src || product.featured_image || '',
      };
    }));
  }

  function configuredGroupFor(handle, kind) {
    const config = Array.from(document.querySelectorAll(`[data-product-alternatives-config][data-product-handle="${CSS.escape(handle)}"][data-product-alternatives-kind="${kind}"]`))
      .map((node) => {
        try { return JSON.parse(node.textContent); } catch (error) { return null; }
      })
      .find((candidate) => candidate?.options?.length);
    return config || null;
  }

  function atcGroupFor(handle) {
    const configured = configuredGroupFor(handle, 'atc');
    const group = configured || Object.values(ATC_GROUPS).find((candidate) => candidate.options.some((option) => option.handle === handle));
    if (!group) return null;
    return groupWithCurrent(group, handle);
  }

  function groupWithCurrent(group, handle) {
    const options = group.options.map((option) => ({ ...option }));
    options.sort((left, right) => Number(right.handle === handle) - Number(left.handle === handle));
    options.forEach((option) => { option.current = option.handle === handle; });
    return { ...group, options };
  }

  function pdpGroupFor(handle) {
    return configuredGroupFor(handle, 'pdp') || PDP_GROUPS[handle] || null;
  }

  function lockPageScroll() {
    lockedScrollY = window.scrollY;
    document.documentElement.classList.add('product-alternatives-scroll-locked');
    document.body.classList.add('product-alternatives-scroll-locked');
    document.body.style.top = `-${lockedScrollY}px`;
  }

  function unlockPageScroll() {
    document.documentElement.classList.remove('product-alternatives-scroll-locked');
    document.body.classList.remove('product-alternatives-scroll-locked');
    document.body.style.removeProperty('top');
    window.scrollTo(0, lockedScrollY);
  }

  function setStickySuppressed(suppressed) {
    document.querySelectorAll('[data-sticky-atc]').forEach((sticky) => {
      sticky.classList.toggle('product-alternatives-sticky-suppressed', suppressed);
      if (suppressed) sticky.setAttribute('aria-hidden', 'true');
    });
  }

  function createOptionButton(option, classPrefix) {
    const isSheetOption = classPrefix === 'product-alternatives-sheet';
    const button = document.createElement(isSheetOption ? 'div' : 'button');
    if (!isSheetOption) button.type = 'button';
    button.className = `${classPrefix}__option`;
    button.dataset.productAlternativeHandle = option.handle;
    if (!isSheetOption) {
      button.disabled = !option.available;
      button.setAttribute('aria-pressed', 'false');
    } else if (!option.available) {
      button.classList.add('is-unavailable');
    }

    if (option.image) {
      const image = document.createElement('img');
      image.className = `${classPrefix}__image`;
      image.src = option.image;
      image.alt = '';
      image.width = classPrefix === 'product-alternatives-sheet' ? 64 : 42;
      image.height = image.width;
      button.append(image);
    }

    const copy = document.createElement('span');
    const label = document.createElement('span');
    label.className = `${classPrefix}__label`;
    label.textContent = option.label || option.product?.title || 'Option';
    const detail = document.createElement('span');
    detail.className = `${classPrefix}__detail`;
    detail.textContent = option.detail || '';
    let offer;
    const price = document.createElement('span');
    price.className = `${classPrefix}__price`;
    const currentPrice = document.createElement('span');
    currentPrice.className = `${classPrefix}__price-current`;
    currentPrice.textContent = money(option.variant?.price);
    price.append(currentPrice);
    if (isSheetOption && Number(option.variant?.compare_at_price) > Number(option.variant?.price)) {
      const comparePrice = document.createElement('span');
      comparePrice.className = `${classPrefix}__price-compare`;
      comparePrice.textContent = money(option.variant.compare_at_price);
      price.append(comparePrice);
    }
    copy.append(label, price, detail);
    if (isSheetOption && option.offer) {
      button.classList.add('has-offer');
      offer = document.createElement('span');
      offer.className = 'product-alternatives-sheet__offer';
      offer.textContent = option.offer;
    }
    if (!option.available) {
      const soldOut = document.createElement('span');
      soldOut.className = `${classPrefix}__sold-out`;
      soldOut.textContent = 'Sold out';
      copy.append(soldOut);
    }
    button.append(copy);
    if (offer) button.append(offer);

    if (isSheetOption) {
      const choose = document.createElement('span');
      choose.className = 'product-alternatives-sheet__choose';
      choose.textContent = option.available ? 'ADD' : 'Sold out';
      button.append(choose);
    }
    return button;
  }

  function replacePdpGallery(section, option) {
    const gallery = section.querySelector('[data-pdp-gallery]');
    const viewport = gallery?.querySelector('.section-product-gallery-main');
    const container = gallery?.querySelector('.section-product-gallery-container');
    const thumbsTrack = gallery?.querySelector('.section-product-gallery-thumbs');
    if (!gallery || !viewport || !container || !thumbsTrack) return;

    const images = Array.from(new Set(option.product?.images || [])).filter(Boolean);
    const sources = images.length ? images : (option.image ? [option.image] : []);
    if (!sources.length) return;

    gallery._numourEmbla?.destroy?.();
    container.replaceChildren();
    thumbsTrack.replaceChildren();

    sources.forEach((source, index) => {
      const slide = document.createElement('div');
      slide.className = 'section-product-gallery-slide';
      slide.dataset.slide = '';
      slide.dataset.mediaId = `${option.handle}-${index}`;
      slide.setAttribute('aria-hidden', String(index !== 0));
      const image = document.createElement('img');
      image.className = 'section-product-gallery-img';
      image.src = source;
      image.alt = option.displayName || option.product?.title || option.label || '';
      image.width = 596;
      image.height = 596;
      image.loading = index === 0 ? 'eager' : 'lazy';
      slide.append(image);
      container.append(slide);

      const thumb = document.createElement('button');
      thumb.className = `section-product-gallery-thumb${index === 0 ? ' is-active' : ''}`;
      thumb.dataset.thumb = String(index);
      thumb.type = 'button';
      thumb.setAttribute('aria-pressed', String(index === 0));
      thumb.setAttribute('aria-label', String(index + 1));
      const thumbImage = document.createElement('img');
      thumbImage.src = source;
      thumbImage.alt = '';
      thumbImage.width = 64;
      thumbImage.height = 64;
      thumbImage.loading = 'lazy';
      thumb.append(thumbImage);
      thumbsTrack.append(thumb);
    });

    const count = sources.length;
    const progress = gallery.querySelector('[data-gallery-progress]');
    const progressFill = gallery.querySelector('[data-gallery-progress-fill]');
    if (progress) {
      progress.dataset.total = String(count);
      progress.hidden = count < 2;
    }
    if (progressFill) progressFill.style.width = count > 1 ? `${100 / count}%` : '100%';
    gallery.querySelectorAll('[data-gallery-prev], [data-gallery-next]').forEach((button) => { button.hidden = count < 2; });
    thumbsTrack.hidden = count < 2;

    if (typeof EmblaCarousel === 'undefined') return;
    const embla = EmblaCarousel(viewport, { loop: count > 1 });
    gallery._numourEmbla = embla;
    const thumbs = Array.from(thumbsTrack.querySelectorAll('[data-thumb]'));
    const select = () => {
      const selected = embla.selectedScrollSnap();
      container.querySelectorAll('[data-slide]').forEach((slide, index) => {
        slide.setAttribute('aria-hidden', String(index !== selected));
      });
      thumbs.forEach((thumb, index) => {
        const active = index === selected;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-pressed', String(active));
      });
      if (progressFill && count > 1) progressFill.style.width = `${(selected + 1) / count * 100}%`;
    };
    embla.on('select', select);
    thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => embla.scrollTo(index)));
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => embla.scrollPrev());
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => embla.scrollNext());
    select();
  }

  function updatePdpPresentation(section, option) {
    section.dataset.selectedAlternativeHandle = option.handle;
    const form = section.querySelector('.section-product-buy-wrap form[data-product-form]');
    const variantInput = form?.querySelector('input[name="id"]');
    if (variantInput) variantInput.value = option.variant.id;

    const price = section.querySelector('[data-product-price]');
    if (price) price.textContent = money(option.variant.price);
    const compare = section.querySelector('[data-product-compare-price]');
    if (compare) {
      const hasCompare = Number(option.variant.compare_at_price) > Number(option.variant.price);
      compare.textContent = hasCompare ? money(option.variant.compare_at_price) : '';
      compare.classList.toggle('hidden', !hasCompare);
    }
    const title = section.querySelector('.section-product-title');
    if (title) {
      if (!section.dataset.baseProductTitle) section.dataset.baseProductTitle = title.textContent.trim();
      title.textContent = option.displayName || section.dataset.baseProductTitle;
    }
    const submit = form?.querySelector('[type="submit"]');
    if (submit) {
      submit.disabled = !option.available;
      const label = submit.querySelector('[data-button-text]');
      if (label) label.textContent = option.available ? 'Add to cart' : 'Sold out';
    }
    const stickyPrice = document.querySelector('[data-sticky-price]');
    if (stickyPrice) {
      const tax = stickyPrice.querySelector('.section-product-sticky-atc-tax');
      // The original Liquid price is a separate text node. Replacing the
      // complete text portion avoids showing both the selected and old price.
      stickyPrice.replaceChildren(document.createTextNode(`${money(option.variant.price)} `));
      if (tax) stickyPrice.append(tax);
    }
    const stickyImage = document.querySelector('[data-sticky-atc] img');
    if (stickyImage && option.image) {
      stickyImage.src = option.image;
      stickyImage.removeAttribute('srcset');
    }
    const stickyName = document.querySelector('.section-product-sticky-atc-name');
    if (stickyName) {
      if (!section.dataset.stickyBaseName) {
        section.dataset.stickyBaseName = Array.from(stickyName.childNodes)
          .filter((node) => node.nodeType === Node.TEXT_NODE)
          .map((node) => node.textContent.trim())
          .filter(Boolean)
          .join(' ') || stickyName.textContent.trim();
      }
      const size = option.displaySize || (option.detail.match(/\b\d+(?:\.\d+)?\s*(?:g|ml)\b/i) || [])[0] || '';
      // Product JSON exposes Shopify's internal/full title. Keep the concise
      // storefront name already rendered by Liquid unless a deliberate display
      // name is configured for the PDP selector.
      stickyName.replaceChildren(document.createTextNode(option.displayName || section.dataset.stickyBaseName));
      if (size) {
        const badge = document.createElement('span');
        badge.className = 'section-product-size-badge';
        badge.textContent = size;
        stickyName.append(' ', badge);
      }
    }
    replacePdpGallery(section, option);
  }

  async function mountPdpSelectors() {
    const sections = Array.from(document.querySelectorAll('[data-product-section][data-product-handle][data-product-alternatives-enabled="true"][data-product-inline-alternatives-enabled="true"]'));
    for (const section of sections) {
      const group = pdpGroupFor(section.dataset.productHandle);
      const buyWrap = section.querySelector('.section-product-buy-wrap');
      if (!group || !buyWrap || section.querySelector('[data-product-type-selector]')) continue;
      let options;
      try {
        options = await hydrateOptions(group);
      } catch (error) {
        console.warn('[product-alternatives] PDP options unavailable', error);
        continue;
      }

      const wrapper = document.createElement('div');
      wrapper.className = 'product-type-selector';
      wrapper.dataset.productTypeSelector = '';
      const heading = document.createElement('p');
      heading.className = 'product-type-selector__heading';
      heading.textContent = group.heading;
      const list = document.createElement('div');
      list.className = 'product-type-selector__options';
      wrapper.append(heading, list);

      const choose = (option) => {
        list.querySelectorAll('button').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.productAlternativeHandle === option.handle));
        });
        updatePdpPresentation(section, option);
      };
      const stickyName = document.querySelector('.section-product-sticky-atc-name');
      if (stickyName && !section.dataset.stickyBaseName) {
        section.dataset.stickyBaseName = Array.from(stickyName.childNodes)
          .filter((node) => node.nodeType === Node.TEXT_NODE)
          .map((node) => node.textContent.trim())
          .filter(Boolean)
          .join(' ') || stickyName.textContent.trim();
      }
      options.forEach((option) => {
        const button = createOptionButton(option, 'product-type-selector');
        button.addEventListener('click', () => choose(option));
        list.append(button);
      });
      buyWrap.prepend(wrapper);
      choose(options.find((option) => option.default && option.available) || options.find((option) => option.available) || options[0]);
    }
  }

  function closeSheet() {
    if (!activeSheet) return;
    activeSheet.remove();
    activeSheet = null;
    unlockPageScroll();
    setStickySuppressed(false);
    sheetTrigger?.focus();
    sheetTrigger = null;
  }

  function cardForm(card) {
    return card?.querySelector('form[data-product-form]');
  }

  function restoreCardAdd(card) {
    if (!card) return;
    const form = cardForm(card);
    const button = form?.querySelector('.product-card-atc-btn');
    if (button) button.hidden = false;
    card.querySelector('.product-card-stepper')?.remove();
    delete card.dataset.selectedAlternativeVariant;
  }

  async function syncSelectedCardSteppers() {
    const cards = Array.from(document.querySelectorAll('[data-product-card][data-selected-alternative-variant]'));
    if (!cards.length || cartSyncInFlight) return;
    cartSyncInFlight = true;
    try {
      const response = await fetch(cartUrl('/cart.js'), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Could not read cart');
      const cart = await response.json();
      cards.forEach((card) => {
        const variantId = String(card.dataset.selectedAlternativeVariant);
        const line = cart.items?.find((item) => String(item.variant_id) === variantId);
        if (!line) {
          restoreCardAdd(card);
          return;
        }
        showCardStepper(card, line.key, variantId, line.quantity);
      });
    } catch (error) {
      console.warn('[product-alternatives] cart sync unavailable', error);
    } finally {
      cartSyncInFlight = false;
    }
  }

  function announceCartUpdate(cartData, variantId) {
    const itemCount = Number(cartData?.item_count);
    if (Number.isFinite(itemCount)) {
      document.querySelectorAll('[data-cart-count], .cart-item-count').forEach((badge) => {
        badge.textContent = String(itemCount);
        badge.dataset.cartCount = String(itemCount);
        badge.toggleAttribute('hidden', itemCount === 0);
      });
      document.querySelectorAll('.flo-cart-icon-bubble').forEach((bubble) => {
        const visibleCount = bubble.querySelector('[aria-hidden="true"]');
        if (visibleCount) visibleCount.textContent = itemCount > 0 ? String(itemCount) : '';
        bubble.toggleAttribute('hidden', itemCount === 0);
      });
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-alternatives',
        productVariantId: variantId,
        cartData,
      });
    }
    document.dispatchEvent(new CustomEvent('cart:refresh', {
      detail: { source: 'product-alternatives', productVariantId: variantId, cartData },
    }));
  }

  function openShopfloCart() {
    if (typeof window.handleFloCartBtn === 'function') {
      window.handleFloCartBtn();
      return;
    }
    document.querySelector('#cart-icon-bubble')?.click();
  }

  async function changeCardQuantity(lineItemKey, quantity, variantId) {
    // Shopflo/cart transforms can replace a line key after an add. Resolve the
    // current Shopify line immediately before each external stepper action so
    // a - click never updates an outdated line or only the visual control.
    const currentResponse = await fetch(cartUrl('/cart.js'), { headers: { Accept: 'application/json' } });
    if (!currentResponse.ok) throw new Error('Could not read current cart');
    const currentCart = await currentResponse.json();
    const currentLine = currentCart.items?.find((item) => item.key === lineItemKey)
      || currentCart.items?.find((item) => String(item.variant_id) === String(variantId));
    if (!currentLine) {
      if (quantity === 0) {
        announceCartUpdate(currentCart, variantId);
        return currentCart;
      }
      throw new Error('Cart item is no longer available');
    }
    const response = await fetch(cartUrl('/cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: currentLine.key, quantity }),
    });
    if (!response.ok) throw new Error('Cart update failed');
    const cart = await response.json();
    announceCartUpdate(cart, variantId);
    // Keep Shopflo's rendered drawer in step with every confirmed card
    // quantity change. The native cart response is already correct here, but
    // Shopflo only refreshes its visible contents when its normal cart entry
    // point is triggered. This applies to decreases and removals too.
    openShopfloCart();
    return cart;
  }

  function showCardStepper(card, lineItemKey, variantId, quantity = 1) {
    const wrap = card.querySelector('.product-card-atc-wrap');
    const form = cardForm(card);
    if (!wrap || !form) return;
    form.querySelector('.product-card-atc-btn')?.setAttribute('hidden', '');
    form.querySelector('input[name="id"]').value = variantId;
    card.dataset.selectedAlternativeVariant = variantId;
    card.querySelector('.product-card-stepper')?.remove();

    const stepper = document.createElement('div');
    stepper.className = 'product-card-stepper';
    const minus = document.createElement('button');
    minus.type = 'button';
    minus.textContent = '−';
    minus.setAttribute('aria-label', 'Decrease quantity');
    const count = document.createElement('output');
    count.textContent = String(quantity);
    const plus = document.createElement('button');
    plus.type = 'button';
    plus.textContent = '+';
    plus.setAttribute('aria-label', 'Increase quantity');
    stepper.append(minus, count, plus);
    wrap.append(stepper);

    const update = async (next) => {
      stepper.querySelectorAll('button').forEach((button) => { button.disabled = true; });
      try {
        if (next < 1) {
          await changeCardQuantity(lineItemKey, 0, variantId);
          if (card) restoreCardAdd(card);
          return;
        }
        const cart = await changeCardQuantity(lineItemKey, next, variantId);
        const line = cart.items?.find((item) => String(item.variant_id) === String(variantId));
        count.textContent = String(line?.quantity || next);
      } finally {
        stepper.querySelectorAll('button').forEach((button) => { button.disabled = false; });
      }
    };
    minus.addEventListener('click', () => update(Math.max(0, Number(count.textContent) - 1)).catch(console.error));
    plus.addEventListener('click', () => update(Number(count.textContent) + 1).catch(console.error));
  }

  function showSheetStepper(row, card, lineItemKey, variantId, quantity = 1) {
    row.querySelector('.product-alternatives-sheet__choose')?.remove();
    row.querySelector('.product-alternatives-sheet__stepper')?.remove();
    const stepper = document.createElement('div');
    stepper.className = 'product-alternatives-sheet__stepper';
    const minus = document.createElement('button');
    minus.type = 'button';
    minus.textContent = '−';
    minus.setAttribute('aria-label', 'Decrease quantity');
    const count = document.createElement('output');
    count.textContent = String(quantity);
    const plus = document.createElement('button');
    plus.type = 'button';
    plus.textContent = '+';
    plus.setAttribute('aria-label', 'Increase quantity');
    stepper.append(minus, count, plus);
    row.append(stepper);

    const update = async (next) => {
      stepper.querySelectorAll('button').forEach((button) => { button.disabled = true; });
      try {
        if (next < 1) {
          await changeCardQuantity(lineItemKey, 0, variantId);
          restoreCardAdd(card);
          closeSheet();
          return;
        }
        const cart = await changeCardQuantity(lineItemKey, next, variantId);
        const line = cart.items?.find((item) => String(item.variant_id) === String(variantId));
        const resolvedQuantity = line?.quantity || next;
        count.textContent = String(resolvedQuantity);
        if (card) showCardStepper(card, line?.key || lineItemKey, variantId, resolvedQuantity);
      } finally {
        stepper.querySelectorAll('button').forEach((button) => { button.disabled = false; });
      }
    };
    minus.addEventListener('click', () => update(Math.max(0, Number(count.textContent) - 1)).catch(console.error));
    plus.addEventListener('click', () => update(Number(count.textContent) + 1).catch(console.error));
  }

  function showPendingSheetStepper(row, choose) {
    choose.remove();
    const stepper = document.createElement('div');
    stepper.className = 'product-alternatives-sheet__stepper is-pending';
    stepper.setAttribute('aria-label', 'Adding one item');
    ['−', '1', '+'].forEach((label, index) => {
      const part = document.createElement(index === 1 ? 'output' : 'button');
      if (index !== 1) {
        part.type = 'button';
        part.disabled = true;
      }
      part.textContent = label;
      stepper.append(part);
    });
    row.append(stepper);
    return stepper;
  }

  async function addFromSheet(option) {
    const response = await fetch(cartUrl('/cart/add.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: option.variant.id, quantity: 1 }),
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || 'Could not add product');
    announceCartUpdate(result, option.variant.id);
    openShopfloCart();
    return result;
  }

  async function openSheet(card, group, trigger = null, section = null) {
    let options;
    try {
      options = await hydrateOptions(group);
    } catch (error) {
      console.warn('[product-alternatives] card options unavailable', error);
      return;
    }
    sheetTrigger = trigger || card?.querySelector('.product-card-atc-btn') || null;
    const sheet = document.createElement('section');
    sheet.className = 'product-alternatives-sheet';
    sheet.setAttribute('role', 'dialog');
    sheet.setAttribute('aria-modal', 'true');
    sheet.setAttribute('aria-label', group.heading);
    const panel = document.createElement('div');
    panel.className = 'product-alternatives-sheet__panel';
    const handle = document.createElement('div');
    handle.className = 'product-alternatives-sheet__handle';
    const header = document.createElement('div');
    header.className = 'product-alternatives-sheet__header';
    const title = document.createElement('h2');
    title.className = 'product-alternatives-sheet__title';
    title.textContent = group.heading;
    const close = document.createElement('button');
    close.className = 'product-alternatives-sheet__close';
    close.type = 'button';
    close.textContent = '×';
    close.setAttribute('aria-label', 'Close variant selector');
    header.append(title);
    const list = document.createElement('div');
    list.className = 'product-alternatives-sheet__options';
    options.forEach((option) => {
      const row = createOptionButton(option, 'product-alternatives-sheet');
      const choose = row.querySelector('.product-alternatives-sheet__choose');
      choose?.addEventListener('click', async () => {
        if (!option.available) return;
        choose.setAttribute('aria-disabled', 'true');
        const pendingStepper = showPendingSheetStepper(row, choose);
        try {
          if (section) updatePdpPresentation(section, option);
          const item = await addFromSheet(option);
          const lineItemKey = item.key || String(option.variant.id);
          if (card) showCardStepper(card, lineItemKey, option.variant.id);
          showSheetStepper(row, card, lineItemKey, option.variant.id);
        } catch (error) {
          console.error('[product-alternatives]', error);
          pendingStepper.remove();
          choose.removeAttribute('aria-disabled');
          choose.textContent = 'ADD';
          row.append(choose);
        }
      });
      list.append(row);
    });
    panel.append(handle, header, list);
    sheet.append(close, panel);
    close.addEventListener('click', closeSheet);
    sheet.addEventListener('click', (event) => { if (event.target === sheet) closeSheet(); });
    activeSheet = sheet;
    document.body.append(sheet);
    lockPageScroll();
    setStickySuppressed(true);
    close.focus();
  }

  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeSheet(); });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => { syncSelectedCardSteppers(); });
  }
  window.addEventListener('focus', syncSelectedCardSteppers);
  window.addEventListener('pageshow', syncSelectedCardSteppers);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') syncSelectedCardSteppers();
  });
  // Shopflo cart removals do not consistently emit the storefront event above.
  // This runs only while a selector card is active, then becomes a no-op.
  window.setInterval(syncSelectedCardSteppers, 1500);

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('.product-card form[data-product-form]');
    if (!form) return;
    const card = form.closest('[data-product-card][data-product-handle]');
    // PDP recommendation cards intentionally keep the store's ordinary
    // direct-add behaviour. Their recommendation sections opt in with this
    // data attribute, so they never invoke the variant drawer.
    if (card?.dataset.productAlternativesDirectAdd === 'true') return;
    if (window.NumourReleaseControls?.homeBestsellersDirectAdd && card?.closest('.bestsellers')) return;
    const group = card && atcGroupFor(card.dataset.productHandle);
    if (!group) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openSheet(card, group, form.querySelector('.product-card-atc-btn'));
  }, true);

  // Capture the sticky click before its normal direct-add listener runs.
  document.addEventListener('click', (event) => {
    const stickyButton = event.target.closest('[data-sticky-atc-btn]');
    if (!stickyButton) return;
    const section = document.querySelector('[data-product-section][data-product-handle]');
    if (section?.dataset.productAlternativesEnabled !== 'true') return;
    const selectedHandle = section.dataset.selectedAlternativeHandle || section.dataset.productHandle;
    // UAT R2 decouples the inline PDP Size / Type UI. Its sticky button must
    // open the separate purchase-options drawer, never that hidden group.
    // Prefer the currently selected product, then its base PDP configuration.
    const baseGroup = atcGroupFor(selectedHandle) || atcGroupFor(section.dataset.productHandle);
    if (!baseGroup) return;
    const group = groupWithCurrent(baseGroup, selectedHandle);
    if (!group) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    // The sticky now opens an ATC purchase selector. Choosing a pack must not
    // rewrite the PDP's identity, gallery, or sticky copy as though it were a
    // Size / Type selection; it only adds the chosen product to the cart.
    openSheet(null, group, stickyButton);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountPdpSelectors, { once: true });
  } else {
    mountPdpSelectors();
  }
})();
